import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, FilePlus2, CornerDownRight } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const SECTION_META = {
  methods: { label: 'Methods', defaultTitle: 'Methods — computational system' },
  system_description: { label: 'System description', defaultTitle: 'System description' },
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export const CodeDraftDialog = ({
  open, onOpenChange, userId, codeProject, section, language, chapters = [], onInserted,
}) => {
  const meta = SECTION_META[section] || SECTION_META.methods;
  const [instructions, setInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [title, setTitle] = useState(meta.defaultTitle);
  const [targetChapter, setTargetChapter] = useState('');
  const [inserting, setInserting] = useState(false);

  const generate = async () => {
    setGenerating(true); setDraft(null);
    try {
      const { job_id } = await thesisApi.startCodeDraft(userId, codeProject.id, {
        section, language, extraInstructions: instructions,
      });
      for (let i = 0; i < 150; i += 1) {
        await wait(4000);
        const job = await thesisApi.getCodeJob(userId, codeProject.id, job_id);
        if (job.status === 'done') { setDraft(job.result); break; }
        if (job.status === 'error') throw new Error(job.error || 'Drafting failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Drafting failed');
    } finally {
      setGenerating(false);
    }
  };

  const insert = async () => {
    setInserting(true);
    try {
      const res = await thesisApi.insertCodeDraft(userId, codeProject.id, {
        title: title || meta.defaultTitle,
        markdown: draft.markdown,
        chapterId: targetChapter || null,
        mode: targetChapter ? 'append' : 'new',
      });
      toast.success(targetChapter ? 'Draft appended to the selected section' : 'New section created in the editor');
      onInserted?.(res.chapter_id);
      onOpenChange(false);
      setDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not insert the draft');
    } finally {
      setInserting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto" data-testid="code-draft-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Draft “{meta.label}” from the Python project</DialogTitle>
          <DialogDescription>
            Grounded only in the {codeProject?.selected_component_name || 'selected component'} claims.
            Markers such as [C3] link each sentence back to a file and line range.
          </DialogDescription>
        </DialogHeader>

        {!draft && (
          <div className="space-y-3">
            <Textarea
              placeholder="Optional instructions for the author's voice, e.g. 'emphasise the YAML-driven risk-factor configuration'"
              value={instructions} onChange={(e) => setInstructions(e.target.value)}
              className="min-h-[90px]" data-testid="code-draft-instructions" />
            <Button onClick={generate} disabled={generating} data-testid="code-draft-generate-btn">
              {generating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Drafting (1–3 min)…</>
                : <>Draft {meta.label}</>}
            </Button>
          </div>
        )}

        {draft && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <Badge variant="outline">{draft.claims_used?.length || 0} / {draft.claims_total} claims used</Badge>
              {(draft.results_gaps || []).length > 0 && (
                <Badge variant="outline" className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30">
                  {draft.results_gaps.length} missing data point(s) for Results
                </Badge>
              )}
            </div>

            <div className="prose prose-sm dark:prose-invert max-w-none min-w-0 break-words border border-border rounded-lg p-4 max-h-[42vh] overflow-auto"
              data-testid="code-draft-preview">
              <ReactMarkdown>{draft.markdown}</ReactMarkdown>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">New section title</label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)}
                  disabled={!!targetChapter} data-testid="code-draft-title-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Or append to an existing section</label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                  value={targetChapter} onChange={(e) => setTargetChapter(e.target.value)}
                  data-testid="code-draft-target-select">
                  <option value="">— create a new section —</option>
                  {chapters.map((c) => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button onClick={insert} disabled={inserting || !draft.markdown} data-testid="code-draft-insert-btn">
                {inserting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : targetChapter ? <CornerDownRight className="w-4 h-4 mr-2" /> : <FilePlus2 className="w-4 h-4 mr-2" />}
                {targetChapter ? 'Append to section' : 'Create section in editor'}
              </Button>
              <Button variant="outline" onClick={() => setDraft(null)} data-testid="code-draft-redo-btn">
                Draft again
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CodeDraftDialog;
